import barba from '@barba/core';
import gsap from 'gsap';

/**
 * Barba.js page transitions.
 *
 * Fixes uit QA:
 *   - scroll naar top vóór mountPage. 'instant' omdat html scroll-behavior: smooth
 *     heeft; anders glijdt de nieuwe pagina zichtbaar omhoog.
 *   - prevent: mailto/tel/anchors/feed toegevoegd.
 *   - body-class sync tussen pages (fix page-template CSS targeting).
 *   - dubbele 'after' fire op initial voorkomen.
 */
export default function initBarba({ mountPage }) {
    // Debug-flag: zet op true om Barba-logs te zien in console tijdens transitie.
    const DEBUG = false;

    barba.init({
        debug: DEBUG,
        preventRunning: true,

        prevent: ({ el, href }) => {
            if (!el || !href) return false;
            // Opt-out attribute: any link with `data-no-barba` skips Barba.
            // The language switcher uses this so the active-language state
            // (rendered server-side) refreshes correctly on locale change.
            if (el.hasAttribute?.('data-no-barba')) return true;
            // Non-http links (mailto, tel, javascript)
            if (/^(mailto:|tel:|javascript:)/i.test(href)) return true;
            // Pure anchor in same page
            if (href.startsWith('#')) return true;
            try {
                const url = new URL(href, window.location.origin);
                if (url.origin !== window.location.origin) return true;
                if (url.pathname.startsWith('/wp-admin'))    return true;
                if (url.pathname.startsWith('/wp-login'))    return true;
                if (url.pathname.startsWith('/wp-json'))     return true;
                if (url.pathname.startsWith('/feed'))        return true;
                if (/\.(xml|pdf|zip|jpg|png|webp|mp4)$/i.test(url.pathname)) return true;
                // Same-page anchor (navigation with only #frag)
                if (url.pathname === window.location.pathname && url.hash) return true;
            } catch (_) { /* noop */ }
            return false;
        },

        transitions: [
            {
                name: 'default-fade',

                async leave({ current }) {
                    // Snappy fade-out met 6px lift omhoog voor richtingsgevoel.
                    await gsap.to(current.container, {
                        opacity: 0,
                        y: -6,
                        duration: 0.2,
                        ease: 'power2.in',
                    });
                },

                async enter({ next }) {
                    // 1. Sync body-class attributes van next document.
                    syncBodyClasses(next);

                    // 2. Scroll afhandelen: naar hash-target als aanwezig, anders naar top.
                    const hash = next.url?.hash || window.location.hash;
                    if (hash) {
                        await new Promise((r) => requestAnimationFrame(r));
                        const target = next.container.querySelector(hash);
                        if (target) {
                            target.scrollIntoView({ block: 'start', behavior: 'instant' });
                        } else {
                            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
                        }
                    } else {
                        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
                    }

                    // 3. Snappy fade+lift entrance. Container start opacity 0 + 8px neer
                    // en gliden in 0.35s naar z'n eindstand.
                    gsap.set(next.container, { opacity: 0, y: 8 });
                    await gsap.to(next.container, {
                        opacity: 1,
                        y: 0,
                        duration: 0.35,
                        ease: 'expo.out',
                        clearProps: 'transform',
                    });
                },
            },
        ],
    });

    // Page-mount hook: fires ook bij initial load (Barba v2 behaviour).
    barba.hooks.afterEnter(({ next }) => {
        try {
            mountPage(next.container);

            window.dispatchEvent(new CustomEvent('irontools:route-change', {
                detail: { namespace: next.namespace, url: next.url },
            }));
        } catch (e) {
            console.error('[irontools] afterEnter error:', e);
        }
    });
}

/**
 * Kopieer de body-class van de next-document zodat CSS die `body.page-id-X`
 * of `body.archive` gebruikt correct blijft werken na Barba-transities.
 */
function syncBodyClasses(next) {
    try {
        if (!next?.html) return;
        const match = next.html.match(/<body[^>]*class="([^"]*)"/i);
        if (match?.[1]) {
            document.body.setAttribute('class', match[1]);
        }
    } catch (e) {
        console.warn('[irontools] body class sync failed:', e);
    }
}
